import type { FormikProps } from 'formik';

import api from '../api';
import type { ClientFormValues } from '../models/Forms';
import { resolveIdentityDocumentValidationCountry } from '../market/resolveIdentityDocumentValidationCountry';

import {
  withInvalidIdentityDocumentRegistryMeta,
  withListedIdentityDocumentValidationMeta,
  withValidatedIdentityDocumentMeta,
  withVerificationFailedIdentityDocumentMeta,
} from './documentRegistryFeedbackState';

type RunClientIdentityDocumentValidationDeps = {
  formikRef: { current: FormikProps<ClientFormValues> | null };
};

export type RunClientIdentityDocumentValidationOutcome =
  | { type: 'completed'; values: ClientFormValues }
  | { type: 'unavailable'; values: ClientFormValues };

function applyIdentityDocumentRegistryMetaToFormik(
  formik: FormikProps<ClientFormValues>,
  values: ClientFormValues,
): ClientFormValues {
  void formik.setFieldValue('identityDocumentValidationStatus', values.identityDocumentValidationStatus);
  void formik.setFieldValue('identityDocumentValidationFingerprint', values.identityDocumentValidationFingerprint);
  if (values.identityDocumentValidationStatus !== 'listed') {
    formik.setFieldError('identificationNumber', undefined);
  }
  return values;
}

function finishRegistryCheck(
  formik: FormikProps<ClientFormValues> | null | undefined,
  values: ClientFormValues,
): RunClientIdentityDocumentValidationOutcome {
  const valuesWithMeta = formik ? applyIdentityDocumentRegistryMetaToFormik(formik, values) : values;
  return { type: 'completed', values: valuesWithMeta };
}

export async function runClientIdentityDocumentValidation(
  values: ClientFormValues,
  { formikRef }: RunClientIdentityDocumentValidationDeps,
): Promise<RunClientIdentityDocumentValidationOutcome> {
  const formik = formikRef.current;
  if (formik) {
    formik.setFieldError('identificationNumber', undefined);
  }

  const country = resolveIdentityDocumentValidationCountry();
  if (!country) {
    return { type: 'completed', values };
  }

  try {
    const response = await api.identityDocumentValidation.create({
      identification: values.identification,
      documentNumber: values.identificationNumber,
      country,
    });

    if (!response.ok || !response.data) {
      return { type: 'unavailable', values };
    }

    const { status } = response.data;

    if (status === 'clear') {
      return finishRegistryCheck(formik, withValidatedIdentityDocumentMeta(values));
    }

    if (status === 'listed') {
      return finishRegistryCheck(formik, withListedIdentityDocumentValidationMeta(values));
    }

    if (status === 'invalid') {
      return finishRegistryCheck(formik, withInvalidIdentityDocumentRegistryMeta(values));
    }

    if (status === 'PROVIDER_ERROR') {
      return finishRegistryCheck(formik, withVerificationFailedIdentityDocumentMeta(values));
    }

    return finishRegistryCheck(formik, withListedIdentityDocumentValidationMeta(values));
  } catch {
    return { type: 'unavailable', values };
  }
}
